"use client"

import { Head } from "@inertiajs/react"

import {
  batyrGroupPhotoBanner,
  batyrTwoFacesSmileBlob,
} from "@/assets/images/photos"
import { Button } from "@/components/ui/button"
import PublicLayout from "@/layouts/public-layout"
import { collabsPath, signUpPath } from "@/routes"

/* PLACEHOLDER COPY: all prose is lorem ipsum, length-matched to the drafted
 * copy so the silhouette holds while staging feedback focuses on layout.
 * The drafted copy is parked in docs/drafts/about-page-copy.md — review and
 * restore it before this page ships anywhere users would read it as true. */

/* Inverted navy masthead, same grammar as the briefs index: Scotch rule,
 * paper-white display type on surface-strong. No highlighter here — this
 * page's single yellow moment is the closing CTA. */
const Masthead = () => (
  <section className="bg-surface-strong border-b border-white/15 py-16 lg:py-24">
    <div className="max-w-container mx-auto flex flex-col gap-2 px-4 lg:px-8">
      <span
        aria-hidden="true"
        className="scotch-rule scotch-rule-action mb-2"
      />
      <p className="text-tertiary_on-brand text-sm font-semibold tracking-[0.12em] uppercase">
        Our Story
      </p>
      <h1 className="text-primary_on-brand text-display-sm lg:text-display-md max-w-3xl font-semibold text-balance">
        Lorem ipsum dolor sit amet consectetur adipiscing elit sed diam
      </h1>
      <p className="text-secondary_on-brand max-w-2xl text-lg text-pretty">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
        tempor incididunt ut labore et dolore magna aliqua — ut enim ad minim
        veniam quis nostrud.
      </p>
    </div>
  </section>
)

/* Magazine-feature story block: intro + two prose columns, adapted from the
 * kit's ContentSectionSimple03. The `prose` class is themed to house tokens
 * in styles/typography.css. */
const StorySection = () => (
  <section className="py-16 md:py-24">
    <div className="max-w-container mx-auto w-full px-4 md:px-8">
      <div className="grid max-w-3xl">
        <span className="text-tertiary text-sm font-semibold">Lorem ipsum</span>
        <h2 className="text-display-sm text-primary mt-3 font-semibold">
          Duis aute irure dolor in reprehenderit velit
        </h2>
        <p className="text-tertiary mt-4 text-lg md:mt-5 md:text-xl">
          Excepteur sint occaecat cupidatat non proident, sunt in culpa qui
          officia deserunt mollit anim id est laborum — sed ut perspiciatis unde
          omnis iste natus error sit.
        </p>
      </div>
      {/* Split-image grammar from the kit's ContentSectionSplitImage01:
          prose column beside a plain rectangular photo, framed like a
          magazine picture — object-cover crop, house radius, no mask. */}
      <div className="mt-12 grid grid-cols-1 items-center gap-12 md:mt-16 md:grid-cols-2 md:gap-16">
        <div className="prose md:prose-lg">
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
            ad minim veniam, quis nostrud exercitation ullamco — laboris nisi,
            ut aliquip, ex ea &ldquo;commodo consequat duis&rdquo; — aute irure
            dolor in reprehenderit in voluptate velit esse cillum dolore eu
            fugiat nulla pariatur, excepteur sint occaecat.
          </p>
          <p>
            Cupidatat non proident sunt in culpa qui officia. Deserunt mollit
            anim id est laborum — sed ut perspiciatis unde omnis, iste natus
            error sit voluptatem accusantium doloremque laudantium et totam rem
            aperiam eaque ipsa quae ab illo inventore veritatis quasi.
          </p>
          <p>
            Architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam
            voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia
            consequuntur magni dolores eos qui ratione voluptatem sequi
            nesciunt. Neque porro quisquam est qui dolorem ipsum quia dolor sit
            amet — consectetur adipisci velit sed quia non numquam eius modi
            tempora incidunt ut labore et dolore.
          </p>
        </div>
        <img
          className="aspect-[4/5] w-full rounded-xl object-cover shadow-xs"
          alt="Four young people laughing together in a studio group photo"
          src={batyrGroupPhotoBanner}
          loading="lazy"
        />
      </div>
    </div>
  </section>
)

/* Manila band: the honest stand-in for a team grid — this is a small
 * initiative, so we introduce the organisation behind it instead. */
const BatyrSection = () => (
  <section className="bg-surface-grouped py-16 md:py-20">
    <div className="max-w-container mx-auto grid w-full grid-cols-1 items-center gap-10 px-4 md:grid-cols-2 md:gap-16 md:px-8">
      {/* The source asset ships with a baked-in blob cutout; zooming inside a
          clipped rectangular frame crops past the irregular edge so it reads
          as a plain photograph. */}
      <div className="aspect-[4/3] w-full overflow-hidden rounded-xl shadow-xs">
        <img
          className="size-full scale-150 object-cover"
          alt="Two young people grinning up at the camera under a blue sky"
          src={batyrTwoFacesSmileBlob}
          loading="lazy"
        />
      </div>
      <div className="flex flex-col items-start gap-4">
        <span className="text-tertiary text-sm font-semibold">
          Lorem ipsum dolor
        </span>
        <h2 className="text-display-xs text-primary md:text-display-sm font-semibold">
          Sed ut perspiciatis unde omnis iste
        </h2>
        <p className="text-tertiary text-lg">
          Lorem ipsum dolor sit amet consectetur adipiscing elit, sed do eiusmod
          tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
          veniam quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
          commodo consequat, duis aute irure dolor in reprehenderit in voluptate
          velit esse.
        </p>
        <Button
          color="secondary"
          size="lg"
          href="https://www.batyr.com.au"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2"
        >
          Learn more about batyr
        </Button>
      </div>
    </div>
  </section>
)

/* Closing CTA, adapted from the kit's CTASimpleCentered. The primary button
 * is the page's one Highlighter Yellow moment. */
const ClosingCta = () => (
  <section className="py-16 md:py-24">
    <div className="max-w-container mx-auto px-4 md:px-8">
      <div className="flex flex-col justify-center text-center">
        <h2 className="text-display-sm text-primary font-semibold">
          Lorem ipsum dolor sit amet
        </h2>
        <p className="text-tertiary mt-4 text-lg md:mt-5">
          Consectetur adipiscing elit, sed do eiusmod tempor incididunt ut
          labore.
        </p>
        <div className="mt-8 flex flex-col-reverse gap-3 self-stretch md:flex-row md:self-center">
          <Button color="secondary" size="xl" href={collabsPath()}>
            Browse the collabs
          </Button>
          <Button size="xl" href={signUpPath()}>
            Start a playlist
          </Button>
        </div>
      </div>
    </div>
  </section>
)

export default function About() {
  return (
    <PublicLayout headerVariant="light">
      <Head title="About" />
      <Masthead />
      <StorySection />
      <BatyrSection />
      <ClosingCta />
    </PublicLayout>
  )
}
